/**
 * Endpoints de **Mantenimiento → Tareas** (`/api/mantenimiento/tareas`).
 *
 * Incluye el ciclo de la tarea (estado, aprobación y rechazo) y las evidencias,
 * que viajan como cuerpo binario porque EDSuite API no usa `multipart`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { env } from '@/config/env';
import { apiClient } from '@/lib/http/api-client';
import { getSessionProvider } from '@/lib/http/session-provider';

import type {
  CierreTareaPayload,
  Evidencia,
  TareaDetalle,
  TareaGuardada,
  TareaPayload,
  TareasCatalogos,
  TareasFiltros,
  TareasRespuesta,
} from './tareas.types';

export const tareasKeys = {
  todas: ['mantenimiento', 'tareas'] as const,
  lista: (filtros: TareasFiltros) => ['mantenimiento', 'tareas', filtros] as const,
  catalogos: ['mantenimiento', 'tareas', 'catalogos'] as const,
  detalle: (id: number) => ['mantenimiento', 'tareas', 'detalle', id] as const,
} as const;

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: TareasFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.categoriaId) parametros.set('categoria_id', String(filtros.categoriaId));
  if (filtros.activoId) parametros.set('activo_id', String(filtros.activoId));
  if (filtros.estado) parametros.set('estado', filtros.estado);
  if (filtros.prioridad) parametros.set('prioridad', filtros.prioridad);
  if (filtros.aprobacion) parametros.set('aprobacion', filtros.aprobacion);

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const tareasApi = {
  listar: (filtros: TareasFiltros = {}, signal?: AbortSignal): Promise<TareasRespuesta> =>
    apiClient.get<TareasRespuesta>(`/api/mantenimiento/tareas${cadenaDe(filtros)}`, { signal }),
  catalogos: (signal?: AbortSignal): Promise<TareasCatalogos> =>
    apiClient.get<TareasCatalogos>('/api/mantenimiento/tareas/catalogos', { signal }),
  detalle: (id: number): Promise<TareaDetalle> =>
    apiClient.get<TareaDetalle>(`/api/mantenimiento/tareas/${id}`),
  crear: (payload: TareaPayload): Promise<TareaGuardada> =>
    apiClient.post<TareaGuardada>('/api/mantenimiento/tareas', { body: payload }),
  cambiarEstado: (id: number, payload: CierreTareaPayload): Promise<TareaGuardada> =>
    apiClient.patch<TareaGuardada>(`/api/mantenimiento/tareas/${id}/estado`, { body: payload }),
  aprobar: (id: number, comentario: string): Promise<TareaGuardada> =>
    apiClient.post<TareaGuardada>(`/api/mantenimiento/tareas/${id}/aprobar`, {
      body: { comentario },
    }),
  rechazar: (id: number, motivo: string): Promise<TareaGuardada> =>
    apiClient.post<TareaGuardada>(`/api/mantenimiento/tareas/${id}/rechazar`, {
      body: { motivo },
    }),
  eliminarEvidencia: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/mantenimiento/evidencias/${id}`),
};

/**
 * Sube una evidencia (cuerpo binario, con el token de la sesión).
 *
 * El nombre original y la descripción viajan como parámetros de la consulta,
 * porque el cuerpo lo ocupa el archivo.
 */
export async function subirEvidencia(
  tareaId: number,
  archivo: File,
  descripcion: string,
): Promise<{ evidencia: Evidencia | null; evidencias: Evidencia[] }> {
  const token = getSessionProvider()?.getAccessToken();
  const parametros = new URLSearchParams({ nombre: archivo.name });

  if (descripcion.trim()) parametros.set('descripcion', descripcion.trim());

  const respuesta = await fetch(
    `${env.apiUrl}/api/mantenimiento/tareas/${tareaId}/evidencias?${parametros.toString()}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': archivo.type || 'application/octet-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: archivo,
    },
  );

  const contenido = (await respuesta.json().catch(() => null)) as
    | { status: boolean; response?: { evidencia: Evidencia | null; evidencias: Evidencia[] }; error?: string }
    | null;

  if (!respuesta.ok || !contenido?.status || !contenido.response) {
    throw new Error(contenido?.error ?? 'No se pudo subir la evidencia.');
  }

  return contenido.response;
}

/** Descarga una evidencia y la guarda con su nombre original. */
export async function descargarEvidencia(evidencia: Evidencia): Promise<void> {
  const token = getSessionProvider()?.getAccessToken();
  const respuesta = await fetch(
    `${env.apiUrl}/api/mantenimiento/evidencias/${evidencia.id}/archivo`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );

  if (!respuesta.ok) throw new Error('No se pudo descargar la evidencia.');

  const blob = await respuesta.blob();
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');

  enlace.href = url;
  enlace.download = evidencia.nombre || evidencia.archivo;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  URL.revokeObjectURL(url);
}

/** Listado de tareas. */
export function useTareas(filtros: TareasFiltros = {}) {
  return useQuery({
    queryKey: tareasKeys.lista(filtros),
    queryFn: ({ signal }) => tareasApi.listar(filtros, signal),
  });
}

/** Catálogos de la pantalla (categorías con activos y sub-activos). */
export function useTareasCatalogos() {
  return useQuery({
    queryKey: tareasKeys.catalogos,
    queryFn: ({ signal }) => tareasApi.catalogos(signal),
    staleTime: 5 * 60 * 1000,
  });
}

/** Detalle de la tarea, con evidencias y sub-activo. */
export function useTareaDetalle(id: number | null) {
  return useQuery({
    queryKey: tareasKeys.detalle(id ?? 0),
    queryFn: () => tareasApi.detalle(id ?? 0),
    enabled: id !== null,
  });
}

/** Invalida listado y detalle tras cualquier cambio. */
function refrescar(cliente: ReturnType<typeof useQueryClient>, id?: number) {
  cliente.invalidateQueries({ queryKey: tareasKeys.todas });
  if (id) cliente.invalidateQueries({ queryKey: tareasKeys.detalle(id) });
  // Las tareas mueven las lecturas de los sub-activos.
  cliente.invalidateQueries({ queryKey: ['mantenimiento', 'sub-activos'] });
}

/** Alta de una tarea. */
export function useCrearTarea() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: tareasApi.crear,
    onSuccess: () => refrescar(cliente),
  });
}

/** Cambio de estado de una tarea (incluye el cierre con lecturas). */
export function useCambiarEstadoTarea() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CierreTareaPayload }) =>
      tareasApi.cambiarEstado(id, payload),
    onSuccess: (_datos, variables) => refrescar(cliente, variables.id),
  });
}

/** Aprobación (sólo administradores). */
export function useAprobarTarea() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comentario }: { id: number; comentario: string }) =>
      tareasApi.aprobar(id, comentario),
    onSuccess: (_datos, variables) => refrescar(cliente, variables.id),
  });
}

/** Rechazo (sólo administradores). */
export function useRechazarTarea() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) =>
      tareasApi.rechazar(id, motivo),
    onSuccess: (_datos, variables) => refrescar(cliente, variables.id),
  });
}

/** Evidencias de una tarea. */
export function useEvidenciasTarea(tareaId: number | null) {
  const cliente = useQueryClient();

  return {
    subir: useMutation({
      mutationFn: ({ archivo, descripcion }: { archivo: File; descripcion: string }) =>
        subirEvidencia(tareaId ?? 0, archivo, descripcion),
      onSuccess: () => refrescar(cliente, tareaId ?? undefined),
    }),
    eliminar: useMutation({
      mutationFn: (id: number) => tareasApi.eliminarEvidencia(id),
      onSuccess: () => refrescar(cliente, tareaId ?? undefined),
    }),
  };
}
